import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { ShoppingCart, PendingActions, Verified, EngineeringOutlined, AccountBalanceWallet } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { clientPortalApi } from '../../api/clientPortal.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');
const inr = (x = 0) => `₹${n(x)}`;

function ChartCard({ title, data, money }: { title: string; data: Array<{ label: string; value: number }>; money?: boolean }) {
  return (
    <Card sx={{ height: '100%' }}><CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.some((d) => d.value > 0) ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} valueFormatter={money ? (v) => inr(v) : undefined} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function ClientPortalDashboardPage() {
  const { data } = useQuery({ queryKey: ['client-portal-dashboard'], queryFn: () => clientPortalApi.dashboard() });
  const o = data?.orders; const i = data?.installations; const f = data?.finance; const c = data?.charts;

  return (
    <>
      <PageHeader title="My Dashboard" subtitle="Your orders, installations and finance (Protection Device Installation)." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Orders" value={n(o?.total)} icon={<ShoppingCart />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Pending Orders" value={n(o?.pending)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Delivered" value={n(o?.completed)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0d9488" label="Installations" value={n(i?.total)} icon={<EngineeringOutlined />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Ongoing Install." value={n(i?.ongoing)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="Outstanding" value={inr(f?.outstanding)} icon={<AccountBalanceWallet />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard color="#1e40af" label="Order Value" value={inr(o?.value)} icon={<ShoppingCart />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#16a34a" label="Paid" value={inr(f?.paid)} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#0284c7" label="Total Invoiced" value={inr(f?.totalInvoiced)} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#d97706" label="Pending Install." value={n(i?.pending)} icon={<PendingActions />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="Order Status" data={c?.orderStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Installation Status" data={c?.installationStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Dealer Performance (installs)" data={c?.dealerPerformance ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Monthly Order Trend" data={c?.orderTrend ?? []} money /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Monthly Payment Trend" data={c?.financeTrend ?? []} money /></Grid>

        <Grid item xs={12}>
          <Card><CardHeader title="Dealer-wise Summary" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.dealerWise.length ?? 0) === 0 ? <EmptyState title="No dealers mapped" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Dealer</TableCell><TableCell>Orders</TableCell><TableCell>Installations</TableCell><TableCell>Outstanding</TableCell><TableCell>Paid</TableCell></TableRow></TableHead>
                  <TableBody>{data!.dealerWise.map((d) => <TableRow key={d.dealerId}><TableCell>{d.dealerName}</TableCell><TableCell>{n(d.orders)}</TableCell><TableCell>{n(d.installations)}</TableCell><TableCell>{inr(d.outstanding)}</TableCell><TableCell>{inr(d.paid)}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
